export const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3000') + '/api';

function extractErrorMessage(data: any, fallbackMessage: string): string {
  if (!data) return fallbackMessage;
  if (typeof data === 'string') return data;
  if (typeof data.message === 'string' && data.message.trim()) return data.message;
  if (typeof data.error === 'string' && data.error.trim()) return data.error;
  if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors.map((err: any) => err?.message || err).join(', ');
  }
  return fallbackMessage;
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (error) {
    console.error('Failed to parse JSON response:', error, text);
    throw new Error('Invalid JSON response received from server.');
  }
}

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

import type { SessionDetail, SessionSummary, SessionFeedback, SavedMessage } from '@/types/session';
import type { UserProfile } from '@/types/user';
import type { UserProfileData, ProfileUpdatePayload } from '@/types/profile';

export interface GiftPlan {
  duration: number; // Always 12 for annual subscription
  months: number; // Always 12 
  price: number; // Always 999
  displayName: string; // "Annual Gift Subscriptions"
  savings: string;
}

export type CompleteOnboardingPayload = {
  displayName: string;
  role: string;
  experience: string;
  industry: string;
  goalStatement: string;
  supportNeed: string;
  recommendedCoach: string;
  selectedCoach: string;
};

// Coupons: validation (public, pre-auth)
export const validateCoupon = async (
  code: string,
  opts: { email?: string; applyPending?: boolean } = {}
): Promise<{ valid: boolean; label?: string; campaign?: string; reason?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/coupons-validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, email: opts.email, applyPending: !!opts.applyPending }),
    }).catch((error) => {
      console.error('Network error in validateCoupon:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<any>(response);
    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to validate code'));
    }
    return (data && typeof data === 'object') ? data : { valid: false };
  } catch (error) {
    console.error('Error validating coupon:', error);
    return { valid: false, reason: 'error' };
  }
};

// Coupons: redeem (authenticated)
export const redeemCoupon = async (
  code: string,
  token: string
): Promise<{ success: boolean; label?: string; campaign?: string; benefit?: { type: string; value: number }; reason?: string }> => {
  try {
    const response = await fetch(`${API_BASE}/coupons-redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ code }),
    }).catch((error) => {
      console.error('Network error in redeemCoupon:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<any>(response);
    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to redeem code'));
    }
    return data;
  } catch (error) {
    console.error('Error redeeming coupon:', error);
    return { success: false, reason: 'error' };
  }
};

export const submitMessageFeedback = async (
  sessionId: string,
  messageId: string,
  coachType: string | undefined,
  feedback: 'helpful' | 'not_helpful',
  token: string,
  extras: { messageSource?: 'coach' | 'integrated'; messageIndex?: number; deviceType?: string; clientTs?: number } = {}
): Promise<{ status: 'recorded' }> => {
  try {
    const response = await fetch(`${API_BASE}/message-feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId,
        messageId,
        coachType,
        feedback,
        messageSource: extras.messageSource,
        messageIndex: extras.messageIndex,
        deviceType: extras.deviceType,
        clientTs: extras.clientTs,
      })
    }).catch((error) => {
      console.error('Network error in submitMessageFeedback:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<{ status: 'recorded' }>(response);
    if (!response.ok) {
      throw new Error(extractErrorMessage(data as any, 'Failed to submit message feedback'));
    }
    return data;
  } catch (error) {
    console.error('Error submitting message feedback:', error);
    throw error;
  }
};

export const getPlanPreferences = async (token: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/plan-preferences`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).catch(error => {
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<any>(response);
    if (!response.ok) {
      throw new Error(extractErrorMessage(payload, 'Failed to fetch plan preferences'));
    }
    return payload;
  } catch (error) {
    console.error('Error in getPlanPreferences:', error);
    throw error;
  }
};

export const updatePlanPreferences = async (prefs: any, token: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/plan-preferences`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prefs),
    }).catch(error => {
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<any>(response);
    if (!response.ok) {
      throw new Error(extractErrorMessage(payload, 'Failed to update plan preferences'));
    }
    return payload;
  } catch (error) {
    console.error('Error in updatePlanPreferences:', error);
    throw error;
  }
};

type SubmitSessionFeedbackPayload = {
  sessionId: string;
  npsScore: number;
  comment?: string | null;
};

type SubmitSessionFeedbackResponse = {
  status: 'recorded';
  feedback: SessionFeedback;
};

export const submitSessionFeedback = async (
  payload: SubmitSessionFeedbackPayload,
  token: string,
): Promise<SubmitSessionFeedbackResponse> => {
  try {
    const response = await fetch(`${API_BASE}/session-feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId: payload.sessionId,
        npsScore: payload.npsScore,
        comment: payload.comment ?? null,
      }),
    }).catch((error) => {
      console.error('Network error in submitSessionFeedback:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<SubmitSessionFeedbackResponse | null>(response);

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to submit feedback'));
    }

    if (!data) {
      throw new Error('Received empty response when submitting feedback');
    }

    return data;
  } catch (error) {
    console.error('Error submitting session feedback:', error);
    throw error;
  }
};

export type CompleteOnboardingResponse = {
  user: UserProfile;
};

export type TokenLog = {
  id: string;
  action_type: 'coaching' | 'mom_generation' | 'insight_scan';
  tokens_consumed: number;
  created_at: string;
  session_id: string | null;
};

export type UsageSummary = {
  month: string;
  // Session-based primary metrics
  sessionLimit: number;
  sessionsUsed: number;
  sessionsRemaining: number;
  sessionUsagePercent: number;
  currentTier: string;
  // Total stats for motivation header
  totalSessions: number;
  totalMinutes: number;
  currentStreak: number;
  // Legacy token metrics for analytics
  tokenLimit: number;
  tokensUsed: number;
  messageCount: number;
  resetAt: string;
  tokensLog?: TokenLog[];
  minutesCoached: number;
  lastSessionDate: string | null;
};

export type GrowthDashboard = {
  coaching_themes: {
    title: string;
    data: string[];
  };
  sentiment_trend: {
    title: string;
    data: { date: string; score: number }[];
  };
  blindspot_alerts: {
    title: string;
    data: { type: string; alert: string }[];
  };
  coach_engagement_mix: {
    title: string;
    data: Record<string, number>;
  };
  reflective_questions: {
    title: string;
    data: { prompt: string; why_it_matters: string; micro_action: string }[];
  };
  mental_models: {
    title: string;
    data: { name: string; summary: string; how_to_apply: string }[];
  };
  journey_metrics: {
    totalSessions: number;
    sessionsWithMoM: number;
    totalChats: number;
    totalActionItems: number;
    activeCoaches: number;
    lastSessionAt: string | null;
  };
  session_timeline: {
    session_id: string;
    occurred_at: string;
    title: string;
    summary: string | null;
    key_takeaways: string[];
    coach_type?: string;
    has_mom: boolean;
  }[];
  mom_highlights: {
    session_id: string;
    title: string;
    key_takeaways: string[];
    occurred_at: string;
  }[];
  analysis_source: string;
  last_insights_refresh?: string | null;
};

export interface AdminOverview {
  userStats: {
    totalUsers: number;
    activeUsers: number;
    newUsers: number;
  };
  usage: {
    totalMessages: number;
    messagesThisMonth: number;
    averageMessagesPerSession: number;
    sessionsLast30Days: { day: string; sessions: number }[];
    peakHours: { hour: number; sessions: number }[];
  };
  coachEngagement: {
    topCoaches: { coachType: string; sessions: number; percentage: number }[];
  };
  demographics: {
    roles: { label: string; count: number }[];
    industries: { label: string; count: number }[];
    experience: { label: string; count: number }[];
  };
  retention: { month: string; newUsers: number }[];
  feedback: {
    general: {
      id: string;
      category: string;
      description: string;
      screenshotUrl: string | null;
      email: string | null;
      status: string;
      createdAt: string;
    }[];
    sessions: {
      id: string;
      npsScore: number;
      comment: string | null;
      submittedAt: string;
      userId: string;
      userName: string | null;
      userEmail: string | null;
    }[];
  };
}

export const getGrowthDashboard = async (token: string): Promise<GrowthDashboard> => {
  try {
    console.log('Fetching growth dashboard with API base:', API_BASE);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // Increased to 90s for deep synthesis

    try {
      const response = await fetch(`${API_BASE}/analytics-dashboard?_t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      }).catch(error => {
        console.error('Network error in getGrowthDashboard:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);
      const payload = await parseJsonResponse<any>(response);

      if (!response.ok) {
        console.error('Server error in getGrowthDashboard:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to fetch growth dashboard'));
      }

      return payload as GrowthDashboard;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error fetching growth dashboard:', error);
    throw error;
  }
};

export const refreshGrowthDashboard = async (token: string): Promise<GrowthDashboard> => {
  try {
    console.log('Refreshing growth dashboard with API base:', API_BASE);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000); // Increased to 90s

    try {
      const response = await fetch(`${API_BASE}/analytics-dashboard`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force_refresh: true }),
        signal: controller.signal,
      }).catch(error => {
        console.error('Network error in refreshGrowthDashboard:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);
      const payload = await parseJsonResponse<any>(response);

      if (!response.ok) {
        console.error('Server error in refreshGrowthDashboard:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to refresh growth dashboard'));
      }

      return payload as GrowthDashboard;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error refreshing growth dashboard:', error);
    throw error;
  }
};

export const completeOnboarding = async (
  payload: CompleteOnboardingPayload,
  token: string,
): Promise<CompleteOnboardingResponse> => {
  try {
    const response = await fetch(`${API_BASE}/onboarding-complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }).catch((error) => {
      console.error('Network error in completeOnboarding:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<CompleteOnboardingResponse | null>(response);

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to complete onboarding'));
    }

    if (!data) {
      throw new Error('Received empty response when completing onboarding');
    }

    return data;
  } catch (error) {
    console.error('Error completing onboarding:', error);
    throw error;
  }
};

export const getCurrentUserProfile = async (token: string): Promise<UserProfile | null> => {
  try {
    const response = await fetch(`${API_BASE}/users/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).catch((error) => {
      console.error('Network error in getCurrentUserProfile:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<UserProfile | null>(response);

    if (response.status === 404) {
      console.warn('User profile not found (404)');
      return null;
    }

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to fetch user profile'));
    }

    return data;
  } catch (error) {
    console.error('Error fetching current user profile:', error);
    throw error;
  }
};

export const getUsageSummary = async (token: string, month?: string): Promise<UsageSummary> => {
  try {
    const searchParams = new URLSearchParams();
    if (month) searchParams.set('month', month);

    const response = await fetch(`${API_BASE}/usage-summary?${searchParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).catch((error) => {
      console.error('Network error in getUsageSummary:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<UsageSummary>(response);

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to fetch usage summary'));
    }

    return data;
  } catch (error) {
    console.error('Error fetching usage summary:', error);
    throw error;
  }
};

// Backwards compatibility for any lingering imports
export const getUserInsights = getGrowthDashboard;

export const saveChatMessage = async (
  sessionId: string | null | undefined,
  messageId: string,
  messageContent: string,
  coachType: string,
  token: string,
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/saved-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ sessionId, messageId, messageContent, coachType }),
    }).catch((error) => {
      console.error('Network error in saveChatMessage:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<any>(response);

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to save message'));
    }

    return data;
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
};

export const saveManualGoal = async (
  messageContent: string,
  coachType: string,
  token: string,
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/saved-message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId: null, // Manual goals are not associated with a session
        messageId: `manual_goal_${Date.now()}`,
        messageContent,
        coachType
      }),
    }).catch((error) => {
      console.error('Network error in saveManualGoal:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const data = await parseJsonResponse<any>(response);

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to save manual goal'));
    }

    return data;
  } catch (error) {
    console.error('Error saving manual goal:', error);
    throw error;
  }
};

export const removeSavedMessage = async (
  sessionId: string | null | undefined,
  messageId: string,
  token: string,
): Promise<any> => {
  try {
    console.log('Removing saved message with API base:', API_BASE);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${API_BASE}/saved-message`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ sessionId, messageId }),
        signal: controller.signal
      }).catch(error => {
        console.error('Network error in removeSavedMessage:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);
      const payload = await parseJsonResponse<any>(response);

      if (!response.ok) {
        console.error('Server error in removeSavedMessage:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to remove saved message'));
      }

      return payload;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error removing saved message:', error);
    throw error;
  }
};
export const listChatSessions = async (token: string, coachType?: string): Promise<SessionSummary[]> => {
  try {
    console.log('Listing chat sessions with API base:', API_BASE);
    const searchParams = new URLSearchParams();
    if (coachType) searchParams.set('coachType', coachType);

    // Add cache buster to avoid stale data
    searchParams.set('_t', Date.now().toString());

    const queryString = searchParams.toString();
    const url = `${API_BASE}/chat-session?${queryString}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout for better reliability

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      }).catch(error => {
        console.error('Network error in listChatSessions:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);
      const payload = await parseJsonResponse<SessionSummary[]>(response);

      if (!response.ok) {
        console.error('Server error in listChatSessions:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to fetch sessions'));
      }

      // Ensure consistent data format for coach types
      const processedSessions = Array.isArray(payload) ? payload.map(session => {
        // Process coachTypes if they're missing or in string format
        if (!session.coachTypes && (session as any).coach_type && typeof (session as any).coach_type === 'string') {
          return {
            ...session,
            coachTypes: (session as any).coach_type.split('|').map((coach: string) => coach.trim()).filter(Boolean)
          };
        }
        return session;
      }) : [];

      console.log(`Retrieved ${processedSessions.length} sessions`);
      return processedSessions;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error listing chat sessions:', error);
    throw error;
  }
};

export const getChatSession = async (sessionId: string, token: string): Promise<SessionDetail | null> => {
  try {
    console.log('Getting chat session with API base:', API_BASE, 'sessionId:', sessionId);

    // Regular API request flow
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout for better reliability

    try {
      const cacheBuster = `&_t=${Date.now()}`;
      const response = await fetch(`${API_BASE}/chat-session?sessionId=${sessionId}${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      }).catch(error => {
        console.error('Network error in getChatSession:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);

      // Check if the response is HTML (which would indicate an error)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        console.error('Received HTML response instead of JSON');
        throw new Error('Invalid response format: received HTML instead of JSON');
      }

      const payload = await parseJsonResponse<SessionDetail | null>(response);
      console.log('Session response status:', response.status, 'payload:', payload ? 'received' : 'null');

      if (response.status === 404) {
        console.warn(`Chat session not found: ${sessionId}`);
        return null;
      }

      if (!response.ok) {
        console.error('Server error in getChatSession:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to fetch session'));
      }

      if (!payload) {
        console.warn('Empty payload received from getChatSession');
        return null;
      }

      // Ensure we handle the message format correctly
      let messages = [];
      if (Array.isArray(payload.messages)) {
        messages = payload.messages;
      } else if (typeof payload.messages === 'string') {
        try {
          // Sometimes messages come as a JSON string that needs parsing
          messages = JSON.parse(payload.messages);
          if (!Array.isArray(messages)) messages = [];
        } catch (e) {
          console.warn('Failed to parse messages string:', e);
          messages = [];
        }
      }

      const savedMessageIds = Array.isArray(payload.savedMessageIds) ? payload.savedMessageIds : [];
      const coachTypes = Array.isArray(payload.coachTypes) ? payload.coachTypes :
        ((payload as any).coach_type ? (payload as any).coach_type.split('|') : []);

      console.log('Processed session detail:', {
        id: payload.id,
        messageCount: messages.length,
        coachTypes
      });

      const rawFeedback = (payload as any).feedback ?? null;
      let feedback: SessionFeedback | null = null;
      if (rawFeedback && typeof rawFeedback === 'object') {
        const mappedScore = typeof rawFeedback.npsScore === 'number' ? rawFeedback.npsScore : Number(rawFeedback.nps_score);
        feedback = {
          id: String(rawFeedback.id),
          sessionId: String(rawFeedback.sessionId ?? rawFeedback.coaching_session_id ?? sessionId),
          npsScore: Number.isFinite(mappedScore) ? mappedScore : 0,
          comment: rawFeedback.comment ?? null,
          submittedAt: String(rawFeedback.submittedAt ?? rawFeedback.submitted_at ?? new Date().toISOString()),
        };
      }

      return {
        ...payload,
        messages,
        savedMessageIds,
        coachTypes,
        feedback,
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error fetching chat session:', error);
    // Return null for session not found
    return null;
  }
};

export const updateChatSession = async (sessionId: string, updates: any, token: string): Promise<SessionDetail | null> => {
  try {
    console.log('Updating chat session with API base:', API_BASE);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${API_BASE}/chat-session/${sessionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
        signal: controller.signal
      }).catch(error => {
        console.error('Network error in updateChatSession:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);
      const payload = await parseJsonResponse<SessionDetail | null>(response);

      if (response.status === 404) {
        console.warn(`Chat session not found for update: ${sessionId}`);
        return null;
      }

      if (!response.ok) {
        console.error('Server error in updateChatSession:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to update session'));
      }

      if (!payload) {
        console.warn('Empty payload received from updateChatSession');
        return null;
      }

      const messages = Array.isArray(payload.messages) ? payload.messages : [];
      const savedMessageIds = Array.isArray(payload.savedMessageIds) ? payload.savedMessageIds : [];

      return {
        ...payload,
        messages,
        savedMessageIds,
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error updating chat session:', error);
    // Return null for session not found
    return null;
  }
};

// Use the mock data functions imported at the top of the file

export const createChatSession = async (coachTypes: string[], token: string, title?: string): Promise<SessionDetail | null> => {
  try {
    console.log('Creating chat session with API base:', API_BASE);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const response = await fetch(`${API_BASE}/chat-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ coachTypes, title }),
        signal: controller.signal
      }).catch(error => {
        console.error('Network error in createChatSession:', error);
        throw new Error(`Network error: ${error.message}`);
      });

      clearTimeout(timeoutId);
      const payload = await parseJsonResponse<SessionDetail | null>(response);

      if (!response.ok) {
        console.error('Server error in createChatSession:', payload);
        throw new Error(extractErrorMessage(payload, 'Failed to create session'));
      }

      if (!payload) {
        console.warn('Empty payload received from createChatSession');
        return null;
      }

      const messages = Array.isArray(payload.messages) ? payload.messages : [];
      const savedMessageIds = Array.isArray(payload.savedMessageIds) ? payload.savedMessageIds : [];

      return {
        ...payload,
        messages,
        savedMessageIds,
      };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    console.error('Error in createChatSession:', error);
    throw error;
  }
};

export const sendMessageToAI = async (
  message: string,
  coachTypes: string[],
  sessionId: string,
  token: string,
  targetCoachId?: string | null,
): Promise<{ replies: any[]; userMessage: any } | null> => {
  try {
    console.log('Sending message to AI with API base:', API_BASE);

    const response = await fetch(`${API_BASE}/ai-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ message, coachTypes, sessionId, targetCoachId }),
    }).catch(error => {
      console.error('Network error in sendMessageToAI:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<{ replies?: any[]; userMessage?: any } | null>(response);

    if (!response.ok) {
      console.error('Server error in sendMessageToAI:', payload);
      throw new ApiError(response.status, extractErrorMessage(payload, 'Failed to send message'), payload);
    }

    if (!payload || !payload.replies || !payload.userMessage) {
      console.warn('Invalid payload received from sendMessageToAI');
      return null;
    }

    return {
      replies: payload.replies,
      userMessage: payload.userMessage,
    };
  } catch (error) {
    console.error('Error in sendMessageToAI:', error);
    throw error;
  }
};

export const getSavedMessages = async (token: string): Promise<SavedMessage[]> => {
  try {
    console.log('Fetching saved messages with API base:', API_BASE);
    const cacheBuster = `?_t=${Date.now()}`;
    const response = await fetch(`${API_BASE}/saved-message${cacheBuster}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    }).catch(error => {
      console.error('Network error in getSavedMessages:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in getSavedMessages:', payload);
      throw new Error(extractErrorMessage(payload, 'Failed to fetch saved messages'));
    }

    return payload.savedMessages || [];
  } catch (error) {
    console.error('Error in getSavedMessages:', error);
    throw error;
  }
};

export const updateSavedMessageMetadata = async (
  payload: {
    id: string;
    priority?: string;
    status?: string;
    timeframe?: string;
    dueDate?: string;
    successMetric?: string;
    emailReminders?: boolean;
    messageContent?: string;
  },
  token: string,
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/saved-message`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await parseJsonResponse<any>(response);

    if (!response.ok) {
      throw new Error(extractErrorMessage(data, 'Failed to update message metadata'));
    }

    return data;
  } catch (error) {
    console.error('Error updating message metadata:', error);
    throw error;
  }
};

export const submitFeedback = async (feedbackData: {
  category: string;
  description: string;
  screenshot?: File;
  captchaToken?: string;
  userEmail?: string;
}): Promise<any> => {
  try {
    console.log('Submitting feedback with API base:', API_BASE);

    // Client-side 2MB validation
    if (feedbackData.screenshot && feedbackData.screenshot.size > 2 * 1024 * 1024) {
      throw new Error('File size exceeds 2MB limit. Please choose a smaller image.');
    }

    let body: FormData | string;
    const headers: Record<string, string> = {};

    if (feedbackData.screenshot) {
      // Use FormData for file upload (browser sets Content-Type with boundary)
      const formData = new FormData();
      formData.append('category', feedbackData.category);
      formData.append('description', feedbackData.description);
      if (feedbackData.userEmail) formData.append('userEmail', feedbackData.userEmail);
      if (feedbackData.captchaToken) formData.append('captchaToken', feedbackData.captchaToken);
      formData.append('screenshot', feedbackData.screenshot);
      body = formData;
    } else {
      // No file — send JSON
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify({
        category: feedbackData.category,
        description: feedbackData.description,
        userEmail: feedbackData.userEmail,
        captchaToken: feedbackData.captchaToken,
      });
    }

    const response = await fetch(`${API_BASE}/feedback`, {
      method: 'POST',
      headers,
      body,
    }).catch(error => {
      console.error('Network error in submitFeedback:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in submitFeedback:', payload);
      throw new Error(extractErrorMessage(payload, 'Failed to submit feedback'));
    }

    return payload;
  } catch (error) {
    console.error('Error in submitFeedback:', error);
    throw error;
  }
};

export const generateMoM = async (sessionId: string, token: string): Promise<any> => {
  try {
    console.log('Generating MoM with API base:', API_BASE);
    const response = await fetch(`${API_BASE}/ai-generate-mom`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ sessionId }),
    }).catch(error => {
      console.error('Network error in generateMoM:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in generateMoM:', payload);
      throw new ApiError(response.status, extractErrorMessage(payload, 'Failed to generate summary'), payload);
    }

    return payload;
  } catch (error) {
    console.error('Error in generateMoM:', error);
    throw error;
  }
};

export const updateUserGoal = async (token: string, goalStatement: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ goalStatement })
    }).catch(error => {
      console.error('Network error in updateUserGoal:', error);
      throw new Error(`Network error: ${error.message}`);
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in updateUserGoal:', payload);
      throw new ApiError(response.status, extractErrorMessage(payload, 'Failed to update goal'), payload);
    }

    return payload;
  } catch (error) {
    console.error('Error in updateUserGoal:', error);
    throw error;
  }
};

// Professional profile update interface
export interface ProfessionalProfileUpdate {
  currentRole?: string;
  goalStatement?: string;
  yearsExperience?: string;
  industry?: string;
}

// Coaching preferences update interface
export interface CoachingPreferencesUpdate {
  goalStatement?: string;
  developmentGoals?: string[];
  supportNeed?: string;
  preferredCoach?: string;
}

/**
 * Fetches the user's profile data (from the user_profile table)
 */
export const getUserProfile = async (token: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in getUserProfile:', payload);
      throw new ApiError(
        response.status,
        extractErrorMessage(payload, 'Failed to fetch profile'),
        payload
      );
    }

    return payload;
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    throw error;
  }
};

/**
 * Updates professional information (role, goal statement, etc.)
 * Uses the profile.js API endpoint with currentRole and goalStatement fields
 */
export const updateProfessionalProfile = async (
  profileData: ProfessionalProfileUpdate,
  token: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        currentRole: profileData.currentRole,
        goalStatement: profileData.goalStatement,
        yearsExperience: profileData.yearsExperience,
        industry: profileData.industry,
        // jobDescription is also supported by profile.js
        jobDescription: (profileData as any).jobDescription
      })
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in updateProfessionalProfile:', payload);
      throw new ApiError(
        response.status,
        extractErrorMessage(payload, 'Failed to update professional profile'),
        payload
      );
    }

    return payload;
  } catch (error) {
    console.error('Error in updateProfessionalProfile:', error);
    throw error;
  }
};

/**
 * Updates coaching preferences (goals, support needs, etc.)
 * Currently uses the same profile.js endpoint but focused on coaching fields
 */
export const updateCoachingPreferences = async (
  preferencesData: CoachingPreferencesUpdate,
  token: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        goalStatement: preferencesData.goalStatement,
        // Include the missing coaching preference fields
        developmentGoals: preferencesData.developmentGoals,
        supportNeed: preferencesData.supportNeed,
        preferredCoach: preferencesData.preferredCoach,
      })
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      console.error('Server error in updateCoachingPreferences:', payload);
      throw new ApiError(
        response.status,
        extractErrorMessage(payload, 'Failed to update coaching preferences'),
        payload
      );
    }

    return payload;
  } catch (error) {
    console.error('Error in updateCoachingPreferences:', error);
    throw error;
  }
};

export const continueSession = async (sessionId: string, token: string) => {
  if (!token || !sessionId) {
    throw new Error('Token and sessionId are required');
  }

  const response = await fetch(`${API_BASE}/session-continue`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sessionId }),
  });

  if (!response.ok) {
    const errorData = await response.text();
    let errorMessage = 'Failed to continue session';

    try {
      const parsedError = JSON.parse(errorData);
      errorMessage = parsedError.message || errorMessage;
    } catch {
      // Use default message if JSON parsing fails
    }

    throw new ApiError(response.status, errorMessage, errorData);
  }

  return parseJsonResponse(response);
};

export const updateNudgeSettings = async (
  data: { 
    coachPersona?: string; 
    nextSessionAt?: string; 
    phoneNumber?: string; 
    timezone?: string;
  },
  token: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const payload = await parseJsonResponse<any>(response);

    if (!response.ok) {
      throw new ApiError(
        response.status,
        extractErrorMessage(payload, 'Failed to update nudge settings'),
        payload
      );
    }

    return payload;
  } catch (error) {
    console.error('Error in updateNudgeSettings:', error);
    throw error;
  }
};
